import React, { useState } from 'react';
import { Zap, RefreshCw, CheckCircle, AlertCircle, Copy } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const AssessmentRecoveryPage = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [permalink, setPermalink] = useState(null);
  const [logs, setLogs] = useState([]);
  
  // Hardcoded recovery data
  const RECOVERY_DATA = {
    userId: 'd9ed0488-0678-4fe5-b77a-a1387ca8ed1f',
    assessmentId: '1b2a4e04-59d0-47ab-8b58-3464674ed172',
    studentName: 'Sheppard'
  };

  const addLog = (message, type = 'info') => {
    setLogs(prev => [...prev, { 
      timestamp: new Date().toLocaleTimeString(), 
      message, 
      type 
    }]);
  };

  const handleRecovery = async () => {
    setIsProcessing(true);
    setStatus(null);
    setMessage('');
    setLogs([]);
    
    try {
      addLog('🚀 Starting emergency recovery for Sheppard...', 'info');
      
      // Step 1: Get assessment data
      addLog('📡 Fetching assessment data from database...', 'info');
      const { data: assessmentData, error: fetchError } = await supabase
        .from('assessment_responses')
        .select('*')
        .eq('id', RECOVERY_DATA.assessmentId)
        .eq('user_id', RECOVERY_DATA.userId)
        .single();

      if (fetchError) throw fetchError;
      if (!assessmentData) throw new Error('Assessment not found');
      
      addLog(`✅ Assessment found: ${Object.keys(assessmentData.responses || {}).length} responses`, 'success');

      // Step 2: Get student profile
      addLog('👤 Fetching student profile...', 'info');
      const { data: profileData } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', RECOVERY_DATA.userId)
        .single();
      
      addLog(`✅ Profile found: ${profileData?.name || 'Student'}`, 'success');

      // Step 3: Generate insights
      addLog('🧠 Calling generate-conversational-insights API...', 'info');
      
      const insightsResponse = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-conversational-insights',
          responses: assessmentData.responses,
          studentProfile: profileData,
          parentContext: null,
          strategicContext: assessmentData.strategic_context || '',
          synthesisInstructions: assessmentData.synthesis_instructions || '',
          initialQuestions: assessmentData.initial_questions || [],
          isEnhancedAssessment: true,
          assessmentId: assessmentData.id,
          userId: assessmentData.user_id,
          studentName: profileData?.name || 'Student'
        })
      });

      if (!insightsResponse.ok) {
        const errorText = await insightsResponse.text();
        throw new Error(`Insights API failed: ${insightsResponse.status} - ${errorText}`);
      }

      const insightsResult = await insightsResponse.json();
      
      if (!insightsResult.success) {
        throw new Error(`Insights generation failed: ${insightsResult.error}`);
      }

      addLog(`✅ Raw insights generated: ${insightsResult.insights.length} characters`, 'success');

      // Step 4: Format insights
      addLog('🎨 Calling format-insights API...', 'info');
      
      const formatResponse = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'format-insights',
          rawText: insightsResult.insights,
          assessmentId: assessmentData.id,
          userId: assessmentData.user_id,
          studentName: profileData?.name || 'Student',
          responses: assessmentData.responses,
          studentProfile: profileData
        })
      });

      if (!formatResponse.ok) {
        const errorText = await formatResponse.text();
        throw new Error(`Format API failed: ${formatResponse.status} - ${errorText}`);
      }

      const formatResult = await formatResponse.json();
      
      if (!formatResult.success) {
        throw new Error(`Insights formatting failed: ${formatResult.error}`);
      }

      addLog('✅ Insights formatted successfully', 'success');

      // Step 5: Save to database
      addLog('💾 Saving complete results to database...', 'info');
      
      const { data: saveData, error: saveError } = await supabase
        .from('assessment_responses')
        .update({
          formatted_insights: formatResult.formattedInsights,
          results_permalink: formatResult.permalink,
          formatted_results_html: formatResult.formattedHtml,
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', RECOVERY_DATA.assessmentId)
        .select()
        .single();

      if (saveError) throw saveError;

      addLog('✅ Results saved successfully!', 'success');
      
      setStatus('success');
      setMessage('Assessment successfully recovered!');
      setPermalink(saveData.results_permalink);
      
      addLog(`🎉 Recovery complete! Permalink: ${saveData.results_permalink}`, 'success');

    } catch (error) {
      console.error('Recovery failed:', error);
      setStatus('error');
      setMessage(error.message);
      addLog(`❌ Recovery failed: ${error.message}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const copyUrl = () => {
    const url = `https://papersedge.co/college/strategic-profile/${permalink}`;
    navigator.clipboard.writeText(url);
    addLog('📋 URL copied to clipboard!', 'success');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-azure-50 to-indigo-100 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              🚨 Emergency Assessment Recovery
            </h1>
            <p className="text-slate-600">
              Recover strategic assessment for student: <span className="font-mono font-semibold">{RECOVERY_DATA.studentName}</span>
            </p>
            <p className="text-sm text-stone-500 mt-1">
              Assessment ID: {RECOVERY_DATA.assessmentId}
            </p>
          </div>

          {/* Action Button */}
          <div className="mb-8">
            <button
              onClick={handleRecovery}
              disabled={isProcessing}
              className={`
                flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-white text-lg
                ${isProcessing 
                  ? 'bg-stone-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 transform hover:scale-105'
                }
                transition-all shadow-lg
              `}
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="w-6 h-6 animate-spin" />
                  <span>Recovering Assessment...</span>
                </>
              ) : (
                <>
                  <Zap className="w-6 h-6" />
                  <span>🚀 START EMERGENCY RECOVERY</span>
                </>
              )}
            </button>
          </div>

          {/* Status Messages */}
          {status === 'success' && (
            <div className="mb-8 bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-emerald-600 mt-1" />
                <div className="flex-1">
                  <p className="text-emerald-800 font-medium text-lg">{message}</p>
                  {permalink && (
                    <div className="mt-4">
                      <p className="text-emerald-700 font-medium mb-2">✅ Student URL Ready:</p>
                      <div className="bg-white border border-emerald-300 rounded-lg p-3 flex items-center justify-between">
                        <code className="text-sm text-slate-800 flex-1 break-all">
                          https://papersedge.co/college/strategic-profile/{permalink}
                        </code>
                        <button
                          onClick={copyUrl}
                          className="ml-3 p-2 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors"
                          title="Copy URL"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-emerald-600 text-sm mt-2">
                        📧 Send this URL to the student immediately!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 mt-1" />
                <div>
                  <p className="text-red-800 font-medium text-lg">Recovery Failed</p>
                  <p className="text-red-600 mt-1">{message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Logs */}
          {logs.length > 0 && (
            <div className="bg-slate-900 rounded-lg p-6">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
                Recovery Log
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {logs.map((log, index) => (
                  <div key={index} className="flex gap-3 text-sm">
                    <span className="text-stone-400 font-mono">{log.timestamp}</span>
                    <span className={`flex-1 ${
                      log.type === 'success' ? 'text-emerald-400' : 
                      log.type === 'error' ? 'text-red-400' : 
                      'text-stone-300'
                    }`}>
                      {log.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentRecoveryPage;